import { eventSchema } from "@fatbees/schemas";
import { toast } from "@fatbees/ui-components";
import { queryCollectionOptions } from "@tanstack/query-db-collection";
import { createCollection } from "@tanstack/react-db";
import { QueryClient } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { deleteEvent, putEvent } from "../api";

const REFETCH_INTERVAL = 1000 * 60 * 15; // 15 minutes
const QUERY_KEY = ["events"];

export function useEventsCollectionInstance(queryClient: QueryClient) {
  useEffect(() => {
    // As of TanStack DB v0.5 refetchInterval is unreliable, even with
    // refetchIntervalInBackground set to true.
    const interval = setInterval(() => {
      queryClient.refetchQueries({ queryKey: QUERY_KEY });
    }, REFETCH_INTERVAL);

    return () => clearInterval(interval);
  }, [queryClient]);

  return useMemo(() => {
    return createCollection(
      queryCollectionOptions({
        id: "events",
        queryKey: QUERY_KEY,
        queryFn: async () => {
          const response = await fetch("/api/events");
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}`);
          }
          const body = await response.json();

          return body.map((event: unknown) => eventSchema.parse(event));
        },
        queryClient,
        getKey: (item) => item.id,
        schema: eventSchema,
        onInsert: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await putEvent(modified);

                collection.utils.writeInsert([modified]);
              }),
            );
          } catch (error) {
            toast({ title: "Could not save event", type: "error" });
            throw error;
          }

          return { refetch: false };
        },
        onUpdate: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await putEvent(modified);

                collection.utils.writeUpdate(modified);
              }),
            );
          } catch (error) {
            toast({ title: "Could not save event", type: "error" });
            throw error;
          }

          return { refetch: false };
        },
        onDelete: async ({ transaction, collection }) => {
          try {
            await Promise.all(
              transaction.mutations.map(async ({ modified }) => {
                await deleteEvent(modified);

                collection.utils.writeDelete(modified.id);
              }),
            );
          } catch (error) {
            toast({ title: "Could not save event", type: "error" });
            throw error;
          }

          return { refetch: false };
        },
      }),
    );
  }, [queryClient]);
}

export type EventsCollection = ReturnType<typeof useEventsCollectionInstance>;
